import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function ForgotPasswordPage() {
  const { resetPassword, error: authError, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const error = localError || authError;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setLocalError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setLocalError(null);

    try {
      await resetPassword(email);
      setEmailSent(true);
    } catch (err) {
      // Error is handled by useAuth
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-orange-hover animate-pulse" />
          <div className="text-text-tertiary text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  // Success screen
  if (emailSent) {
    return (
      <div className="min-h-screen bg-surface-base flex items-center justify-center p-6 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-accent/5 blur-3xl" />
          <div className="absolute bottom-[-30%] right-[-20%] w-[700px] h-[700px] rounded-full bg-weekly/5 blur-3xl" />
        </div>

        <div className="relative w-full max-w-[420px] animate-in">
          <div className="absolute -inset-1 bg-gradient-to-r from-success/20 via-transparent to-accent/20 rounded-3xl blur-xl opacity-50" />

          <div className="relative bg-surface-card/80 backdrop-blur-xl rounded-2xl p-8 md:p-10 border border-border shadow-xl text-center">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-success/20 flex items-center justify-center">
                <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-text-primary mb-3">Check your email</h2>
            <p className="text-text-secondary mb-6">
              We've sent a password reset link to <span className="text-accent font-medium">{email}</span>.
              Click the link in the email to reset your password.
            </p>

            <div className="bg-surface-base/50 rounded-xl p-4 mb-6">
              <p className="text-text-muted text-sm">
                Didn't receive the email? Check your spam folder or try again with a different email.
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                className="w-full py-3 px-6 bg-surface-base border border-border text-text-primary rounded-xl text-[15px] font-medium hover:bg-surface-hover transition-all"
              >
                Try another email
              </button>

              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-accent hover:text-orange-hover transition-colors font-medium"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-base flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-accent/5 blur-3xl" />
        <div className="absolute bottom-[-30%] right-[-20%] w-[700px] h-[700px] rounded-full bg-weekly/5 blur-3xl" />
        <div className="absolute top-[40%] right-[10%] w-[300px] h-[300px] rounded-full bg-monthly/5 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Forgot Password Card */}
      <div className="relative w-full max-w-[420px] animate-in">
        <div className="absolute -inset-1 bg-gradient-to-r from-accent/20 via-transparent to-weekly/20 rounded-3xl blur-xl opacity-50" />

        <div className="relative bg-surface-card/80 backdrop-blur-xl rounded-2xl p-8 md:p-10 border border-border shadow-xl">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-text-primary mb-2">Reset your password</h1>
            <p className="text-text-tertiary text-sm">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm p-4 rounded-xl mb-6 animate-in">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {error}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-1.5">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLocalError(null);
                }}
                className="w-full px-4 py-3 bg-surface-base border border-border rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
                placeholder="you@company.com"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="
                group relative w-full py-3.5 px-6 mt-2
                bg-gradient-to-r from-accent to-orange-hover
                text-surface-base rounded-xl
                text-[15px] font-semibold
                cursor-pointer flex items-center justify-center gap-2
                border-none
                shadow-lg shadow-accent/25
                hover:shadow-xl hover:shadow-accent/30
                hover:scale-[1.02]
                active:scale-[0.98]
                transition-all duration-200
                disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100
              "
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </>
              ) : (
                'Send reset link'
              )}

              {/* Shine effect */}
              {!isSubmitting && (
                <div className="absolute inset-0 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                </div>
              )}
            </button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to sign in
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom attribution */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-text-muted/50 text-xs">
        Helm
      </div>
    </div>
  );
}
